import { inject, isDevMode } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { APP_CONFIGS, AppNavigationService } from '@dental-ui/shared/app-navigation';
import { LAB_APP_NAME, LAB_ENTRY_PATH_RELEASE } from '@dental-ui/shared/consts';

const ACCOUNT_RELEASE_ROUTES = [
  '/account',
  '/account/subscriptions',
  '/account/organization',
  '/account/team',
  '/account/settings',
] as const;

const LAB_RELEASE_ROUTES = ['/design-projects'] as const;

/**
 * Development builds (`ng serve`) and deployed dev hosts (`dev-account.autobar.design`) stay open.
 * A production build on a main-style host is closed.
 */
export function isDevelopmentBuild(): boolean {
  if (isDevMode()) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const label = window.location.hostname.toLowerCase().split('.')[0] ?? '';
  return label === 'dev' || label.startsWith('dev-');
}

export function isReleaseRoute(route: string): boolean {
  const path = normalizePath(route);
  return matchesRoute(path, ACCOUNT_RELEASE_ROUTES) || matchesRoute(path, LAB_RELEASE_ROUTES);
}

/**
 * Blocks product routes outside the release set when the app is not a development build.
 * Clinic has no release routes: on main it leaves the clinic app for Lab design-projects.
 * Development builds pass through.
 */
export const developmentGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  if (isDevelopmentBuild()) {
    return true;
  }

  if (isClinicHost()) {
    const appNavigation = inject(AppNavigationService, { optional: true });
    appNavigation?.navigateToApp(LAB_APP_NAME, LAB_ENTRY_PATH_RELEASE);
    return false;
  }

  const router = inject(Router);
  const path = normalizePath(state.url);
  if (path.startsWith('/account') || path.startsWith('/profile')) {
    return router.createUrlTree(['/account']);
  }

  return router.createUrlTree(['/design-projects']);
};

function isClinicHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const clinic = APP_CONFIGS['clinic'];
  const port = window.location.port;
  if (clinic?.devPort && port === String(clinic.devPort)) {
    return true;
  }

  const label = window.location.hostname.toLowerCase().split('.')[0] ?? '';
  const subdomain = (clinic?.subdomain ?? 'clinic').toLowerCase();
  return label === subdomain || label === `dev-${subdomain}`;
}

function matchesRoute(path: string, routes: readonly string[]): boolean {
  return routes.some((route) => {
    if (route === '/account') {
      return path === '/account';
    }
    return path === route || path.startsWith(`${route}/`);
  });
}

function normalizePath(url: string): string {
  const withoutQuery = url.split('?')[0]?.split('#')[0] ?? '';
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery || '/';
}
